import { calculateBlockedDuration } from '@exam-center/blocked-user-management';
import { BlockedRecordBlockedScope } from '@exam-center/graphql';
import { ChipGroup, Grid, Text } from '@shared/components';
import { dayjs, formatDate } from '@shared/utils';
import React from 'react';

import {
  BlockedRecordAccordion,
  type BlockedRecordAccordionProps,
} from '../blocked-record-accordion';

interface Content {
  label: string;
  value: string | JSX.Element;
}

type Mode = 'exam-passer' | 'cheater';

function getLabelByMode(mode: Mode) {
  switch (mode) {
    case 'exam-passer':
    case 'cheater':
      return 'ประเภทหลักสูตรที่ถูกระงับสิทธิ์สอบ';
    default:
      return '';
  }
}

function getTitleByMode(
  mode: Mode,
  blockedScope: BlockedRecordBlockedScope,
  examCourseInfos: string[] | null,
) {
  switch (mode) {
    case 'exam-passer':
      return blockedScope === BlockedRecordBlockedScope.AllScope
        ? 'ทุกหลักสูตร'
        : (examCourseInfos?.join('\n') ?? '-');
    case 'cheater':
      return blockedScope === BlockedRecordBlockedScope.AllScope ? 'ทุกหลักสูตร' : 'บางหลักสูตร';
    default:
      return '';
  }
}

function getTypeByMode(mode: Mode) {
  switch (mode) {
    case 'exam-passer':
      return 'ผู้ที่สอบผ่าน';
    case 'cheater':
      return 'ผู้ทุจริต';
    default:
      return '';
  }
}

function renderExamCourses(
  blockedScope: BlockedRecordBlockedScope,
  examCourseInfos: string[] | null,
) {
  if (blockedScope === BlockedRecordBlockedScope.AllScope) {
    return 'ระงับสิทธิ์ทุกหลักสูตร';
  }

  return (
    <ChipGroup
      labels={examCourseInfos ?? []}
      limit={2}
      component='chip'
      slotProps={{
        wrapper: {
          sx: {
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
        },
        elementsWrapper: {
          sx: (theme) => ({
            gap: theme.spacing(2),
          }),
        },
        chip: {
          size: 'medium',
          sx: {
            alignSelf: 'flex-start',
          },
        },
      }}
    />
  );
}

export interface BlockedRecordExamineeAccordionProps
  extends Pick<BlockedRecordAccordionProps, 'endAdornment'> {
  blockedScope: BlockedRecordBlockedScope;
  blockedAt: Date;
  blockedUntil: Date | null;
  blockedReason: string | null;
  examCenterCode: string | null;
  examCenterName: string | null;
  examCourseInfos: string[] | null;
  examRound: number;
  examStartedAt: Date;
  examEndedAt: Date;
  mode: Mode;
}

export default function BlockedRecordExamineeAccordion(
  props: Readonly<BlockedRecordExamineeAccordionProps>,
) {
  const {
    mode,
    blockedUntil,
    blockedAt,
    blockedScope,
    blockedReason,
    examCenterCode,
    examCenterName,
    examCourseInfos,
    examRound,
    examStartedAt,
    examEndedAt,
    endAdornment,
  } = props;

  const content: Array<Content> = [
    {
      label: 'ประเภทผู้ถูกระงับสิทธิ์',
      value: getTypeByMode(mode),
    },
    {
      label: 'วันที่เริ่มระงับสิทธิ์',
      value: formatDate(blockedAt),
    },
    {
      label: 'วันที่สิ้นสุดระงับสิทธิ์',
      value: blockedUntil ? formatDate(blockedUntil) : '-',
    },
    mode === 'cheater' && blockedUntil
      ? {
          label: 'ระยะเวลาที่ถูกระงับสิทธิ์',
          value: calculateBlockedDuration(blockedAt, blockedUntil),
        }
      : null,
    {
      label: 'หลักสูตรที่ระงับสิทธิ์สอบ',
      value: renderExamCourses(blockedScope, examCourseInfos),
    },
    {
      label: 'ศูนย์ทดสอบ',
      value: examCenterCode ? `${examCenterCode}: ${examCenterName ?? ''}` : '-',
    },
    {
      label: 'หมายเหตุ',
      value: blockedReason ?? '-',
    },
    {
      label: 'รอบสอบ',
      value: `${examRound}`,
    },
    {
      label: 'วันที่และเวลาสอบ',
      value: `${dayjs(examStartedAt).format('วันddddที่ DD-MM-YYYY, HH:mm')} - ${dayjs(examEndedAt).format('HH:mm')}`,
    },
  ].filter((item): item is Content => item !== null);

  return (
    <BlockedRecordAccordion
      label={getLabelByMode(mode)}
      endAdornment={endAdornment}
      title={getTitleByMode(mode, blockedScope, examCourseInfos)}
      blockedUntil={blockedUntil}
      examCenterCode={examCenterCode}
    >
      <Grid
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(4),
          gridTemplateColumns: '240px 1fr',
        })}
      >
        {content.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Text variant='body1Semibold'>{label}</Text>
            {typeof value === 'string' ? <Text>{value}</Text> : value}
          </React.Fragment>
        ))}
      </Grid>
    </BlockedRecordAccordion>
  );
}
